import React, { useState, useEffect } from 'react';
import { User, Mail, Phone, GraduationCap, Book, MessageSquare, CheckCircle } from 'lucide-react';

interface FormData {
  name: string;
  email: string;
  contact: string;
  university: string;
  course: string;
  message: string;
}

export const RegistrationSection = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    contact: '',
    university: '',
    course: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  useEffect(() => {
    if (submitSuccess) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [submitSuccess]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(false);

    try {
      const formUrl = 'https://docs.google.com/forms/d/e/1FAIpQLScTjiqlbHYuPFDPwvOpDMiR-pJYVTD8vv-c9l3Zw5Q4k2Ktsw/formResponse';

      const formBody = new URLSearchParams({
        'entry.1531441291': formData.name,
        'entry.623624654': formData.email,
        'entry.1542520100': formData.contact,
        'entry.105744187': formData.university,
        'entry.238190990': formData.course,
        'entry.751590235': formData.message
      });

      await fetch(formUrl, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody
      });

      setSubmitSuccess(true);
      setFormData({
        name: '',
        email: '',
        contact: '',
        university: '',
        course: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-6 md:py-8" style={{backgroundColor: '#FAF7F2'}}>
      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-6 md:mb-8">
            <div className="inline-flex items-center px-3 md:px-4 py-2 md:py-2 bg-white rounded-full border-2 font-bold mb-2 md:mb-3 shadow-lg" style={{borderColor: '#C4A052', color: '#6B4423'}}>
              <img 
                src="/A2ZAssist%20logo.png" 
                alt="A2Z Assist Logo" 
                className="w-4 h-4 md:w-5 md:h-5 mr-2 md:mr-3 object-contain"
              />
              Join Our Community
            </div>
            <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold mb-3 md:mb-4" style={{color: '#2C2420'}}>
              Register for A2ZCampus™ Workshop
            </h2>
            <p className="text-sm md:text-base max-w-4xl mx-auto px-2" style={{color: '#2C2420'}}>
              Interested in our proprietary learning tools and training programs? Fill out the form below to register 
              your interest and be the first to know about upcoming ClarityKit™, Startup Simulator™, and Real World Readiness™ sessions.
            </p>
          </div>
          
          <div className="bg-white rounded-3xl shadow-2xl p-3 md:p-5 lg:p-6 border border-gray-200">
            {submitSuccess ? (
              <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 px-4">
                <div className="text-white px-5 md:px-6 py-6 md:py-8 rounded-2xl text-center max-w-md w-full shadow-2xl" style={{backgroundColor: '#C4A052'}}>
                  <CheckCircle className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-3 md:mb-4 text-white" />
                  <h3 className="text-xl md:text-2xl font-bold mb-3 md:mb-4">Registration Successful!</h3>
                  <p className="text-sm md:text-base mb-2 md:mb-3">Thank you for your interest in A2ZCampus™ workshops.</p>
                  <p className="text-sm md:text-base mb-4 md:mb-6">We'll contact you soon with more details about our proprietary learning tools.</p>
                  <p className="text-xs text-gray-200 mb-4 md:mb-6">- Team A2ZCampus™</p>
                  <button 
                    onClick={() => setSubmitSuccess(false)}
                    className="px-5 md:px-6 py-2 md:py-2 bg-white rounded-lg hover:bg-gray-100 transition-colors font-semibold transform hover:scale-105 text-sm md:text-base"
                    style={{color: '#C4A052'}}
                  >
                    Register Another
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-3 md:space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                  <div>
                    <label htmlFor="name" className="flex items-center text-sm font-medium mb-2" style={{color: '#6B4423'}}>
                      <User className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" style={{color: '#C4A052'}} />
                      Full Name <span className="text-red-500 ml-1">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-3 md:px-3 py-2 md:py-3 rounded-xl border focus:ring-2 focus:border-transparent transition-all text-sm md:text-base"
                      style={{borderColor: '#6B4423', '--tw-ring-color': '#6B4423'} as React.CSSProperties}
                      placeholder="Enter your full name"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="flex items-center text-sm font-medium mb-2" style={{color: '#6B4423'}}>
                      <Mail className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" style={{color: '#C4A052'}} />
                      Email Address <span className="text-red-500 ml-1">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-3 md:px-3 py-2 md:py-3 rounded-xl border focus:ring-2 focus:border-transparent transition-all text-sm md:text-base"
                      style={{borderColor: '#6B4423', '--tw-ring-color': '#6B4423'} as React.CSSProperties}
                      placeholder="you@example.com"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                  <div>
                    <label htmlFor="contact" className="flex items-center text-sm font-medium mb-2" style={{color: '#6B4423'}}>
                      <Phone className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" style={{color: '#C4A052'}} />
                      Contact Number
                    </label>
                    <input
                      type="tel"
                      id="contact"
                      name="contact"
                      value={formData.contact}
                      onChange={handleChange}
                      className="w-full px-3 md:px-3 py-2 md:py-3 rounded-xl border focus:ring-2 focus:border-transparent transition-all text-sm md:text-base"
                      style={{borderColor: '#6B4423', '--tw-ring-color': '#6B4423'} as React.CSSProperties}
                      placeholder="Your phone number"
                    />
                  </div>

                  <div>
                    <label htmlFor="university" className="flex items-center text-sm font-medium mb-2" style={{color: '#6B4423'}}>
                      <GraduationCap className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" style={{color: '#C4A052'}} />
                      University/College <span className="text-red-500 ml-1">*</span>
                    </label>
                    <input
                      type="text"
                      id="university"
                      name="university"
                      value={formData.university}
                      onChange={handleChange}
                      required
                      className="w-full px-3 md:px-3 py-2 md:py-3 rounded-xl border focus:ring-2 focus:border-transparent transition-all text-sm md:text-base"
                      style={{borderColor: '#6B4423', '--tw-ring-color': '#6B4423'} as React.CSSProperties}
                      placeholder="Your institution name"
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="course" className="flex items-center text-sm font-medium mb-2" style={{color: '#6B4423'}}>
                    <Book className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" style={{color: '#C4A052'}} />
                    Course/Degree <span className="text-red-500 ml-1">*</span>
                  </label>
                  <input
                    type="text"
                    id="course"
                    name="course"
                    value={formData.course}
                    onChange={handleChange}
                    required
                    className="w-full px-3 md:px-3 py-2 md:py-3 rounded-xl border focus:ring-2 focus:border-transparent transition-all text-sm md:text-base"
                    style={{borderColor: '#6B4423', '--tw-ring-color': '#6B4423'} as React.CSSProperties}
                    placeholder="e.g., Computer Science, Engineering, etc."
                  />
                </div>
                
                <div>
                  <label htmlFor="message" className="flex items-center text-sm font-medium mb-2" style={{color: '#6B4423'}}>
                    <MessageSquare className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" style={{color: '#C4A052'}} />
                    Message (Optional)
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-3 md:px-3 py-2 md:py-3 rounded-xl border focus:ring-2 focus:border-transparent transition-all resize-none text-sm md:text-base"
                    style={{borderColor: '#6B4423', '--tw-ring-color': '#6B4423'} as React.CSSProperties}
                    placeholder="Tell us about your interest in our proprietary learning tools or any specific questions you have"
                  ></textarea>
                </div>
                
                {submitError && (
                  <div className="p-2 md:p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm md:text-base">
                    There was an error submitting your registration. Please try again or contact us directly.
                  </div>
                )}
                
                <div className="text-center">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`px-6 md:px-8 py-2 md:py-3 text-white font-semibold rounded-xl shadow-lg hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-opacity-50 transition-all transform hover:scale-105 text-sm md:text-base ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                    style={{backgroundColor: '#C4A052', '--tw-ring-color': '#C4A052'} as React.CSSProperties}
                  >
                    {isSubmitting ? 'Submitting...' : 'Register for A2ZCampus™'}
                  </button>
                </div>
                
                <p className="mt-2 md:mt-3 text-xs md:text-sm text-center" style={{color: '#2C2420'}}>
                  By submitting this form, you agree to receive communications about A2ZCampus™ workshops and programs.
                </p>
              </form>
            )}
          </div>
          
          <div className="mt-6 md:mt-8 text-center">
            <h3 className="text-base md:text-lg font-bold mb-2 md:mb-3" style={{color: '#2C2420'}}>Alternative Registration Options</h3>
            <p className="mb-3 md:mb-4 text-sm md:text-base px-2" style={{color: '#2C2420'}}>
              You can also register by sending an email directly to{' '}
              <a href="mailto:info@a2zassist.com" className="hover:underline font-medium" style={{color: '#7A9CB5'}}>
                info@a2zassist.com
              </a>{' '}
              with your details.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};